import { webrtcVoiceService } from '../services/webrtcService';
import { useClient } from '../contexts/ClientContext';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

import React, { useState, useEffect } from 'react';
import { Medium } from '../types';
import { StarIcon, PhoneIcon, MessageCircleIcon, InfoIcon, HeartIcon, CrownIcon } from './Icons';
import { isMediumFavorite, toggleFavoriteMedium } from './favorites';
import { isMediumVip } from '../data/mediums';

interface MediumProfileModalProps {
    medium: Medium;
    onClose: () => void;
    onChatClick: (medium: Medium) => void;
}

const MediumProfileModal: React.FC<MediumProfileModalProps> = ({ medium, onClose, onChatClick }) => {
    const { client, openAuthModal, addReview, getRealReviews } = useClient();
    
    const isVip = isMediumVip(medium);
    const clientPoints = client?.loyaltyPoints || 0;
    const isVipLocked = isVip && (!client || clientPoints < 1000);
    
    const [isFav, setIsFav] = useState(false);

    useEffect(() => {
        setIsFav(isMediumFavorite(medium.id));
        const handleFavUpdate = () => {
            setIsFav(isMediumFavorite(medium.id));
        };
        window.addEventListener('astro_favorites_updated', handleFavUpdate);
        return () => window.removeEventListener('astro_favorites_updated', handleFavUpdate);
    }, [medium.id]);

    const handleToggleFavorite = () => {
        if (!client) {
            openAuthModal();
            return;
        }
        toggleFavoriteMedium(medium.id);
    };

    // États pour le formulaire d'avis
    const [rating, setRating] = useState(5);
    const [reviewText, setReviewText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showReviewForm, setShowReviewForm] = useState(false);

    // État pour les onglets MOBILE
    const [mobileTab, setMobileTab] = useState<'bio' | 'reviews'>('bio');

    // Récupérer les vrais avis stockés localement
    const realReviews = getRealReviews(medium.id);
    
    // Fusionner les avis et TRIER PAR TIMESTAMP (Du plus récent au plus ancien)
    // FILTRE : On ne garde que les avis qui ont du texte (les notes seules d'invités sont masquées de la liste)
    const allReviews = [...realReviews, ...medium.reviewsList]
        .filter(r => r.text && r.text.trim() !== "") 
        .sort((a, b) => {
            const timeA = a.timestamp || 0;
            const timeB = b.timestamp || 0;
            return timeB - timeA;
        });

    const handleSubmitReview = (e: React.FormEvent) => {
        e.preventDefault();
        if (!reviewText.trim()) return;
        setIsSubmitting(true);

        setTimeout(() => {
            addReview(medium.id, rating, reviewText);
            setReviewText('');
            setIsSubmitting(false);
            setShowReviewForm(false);
        }, 1000);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-0 sm:p-3 md:p-6 animate-fade-in">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose}></div>

            {/* Modal Content */}
            <div className="relative bg-gradient-to-b from-mystic-900 to-black w-full h-full sm:h-[90vh] max-h-[100dvh] max-w-4xl rounded-none sm:rounded-2xl shadow-[0_0_50px_rgba(139,92,246,0.3)] border-0 sm:border border-mystic-700 flex flex-col md:flex-row overflow-hidden">
                
                {/* Close Button */}
                <button 
                    onClick={onClose}
                    className="absolute top-3 right-3 text-white/50 hover:text-white z-50 bg-black/60 rounded-full p-2 hover:bg-black/90 transition-all border border-white/10"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>

                {/* Favorite Button */}
                <button 
                    onClick={handleToggleFavorite}
                    className={`absolute top-3 left-3 z-50 flex items-center gap-1.5 px-3.5 py-2 rounded-full backdrop-blur-md transition-all duration-300 shadow-lg border text-xs font-bold ${
                        isFav 
                        ? 'bg-pink-600/90 text-white border-pink-400 shadow-[0_0_15px_rgba(236,72,153,0.5)]' 
                        : 'bg-black/60 text-gray-300 border-white/20 hover:text-white hover:bg-black/80'
                    }`}
                    title={isFav ? "Retirer de vos favoris" : "Ajouter à vos favoris"}
                >
                    <HeartIcon className={`w-4 h-4 ${isFav ? 'fill-current text-white' : ''}`} />
                    <span>{isFav ? 'Dans vos favoris' : 'Ajouter aux favoris'}</span>
                </button>

                {/* Left Column: Photo & Main Info (Toujours visible en haut sur mobile) */}
                <div className="w-full md:w-1/3 bg-mystic-800 p-4 md:p-6 flex flex-col items-center text-center border-b md:border-b-0 md:border-r border-mystic-700 shrink-0 overflow-y-auto max-h-[40vh] md:max-h-full">
                    <div className="relative mb-3 md:mb-4 shrink-0">
                        <img 
                            src={medium.imageUrl} 
                            alt={medium.name} 
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }}
                            className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-gold-500 object-cover shadow-xl"
                        />
                        <div className={`absolute bottom-1 right-1 md:bottom-2 md:right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 border-mystic-800 ${medium.status === 'available' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    </div>
                    <h2 className="text-xl md:text-2xl font-serif font-bold text-white mb-1">{medium.name}</h2>
                    <p className="text-purple-300 text-xs md:text-sm font-medium mb-2 md:mb-4">{medium.specialties.join(" • ")}</p>
                    
                    <div className="flex items-center gap-1 mb-4 bg-black/30 px-3 py-1 rounded-full border border-white/10">
                        <div className="flex text-gold-400">
                            {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-3 h-3 ${i < Math.floor(medium.rating) ? 'fill-current' : 'text-gray-600'}`} />)}
                        </div>
                        <span className="text-xs text-gray-300">({medium.reviews} consult.)</span>
                    </div>

                    <div className="w-full space-y-2 md:space-y-3 mt-auto">
                        <div className="relative">
                            <button 
                                type="button"
                                onClick={() => {
                                    onClose();
                                    const currentClient = client || {
                                        id: 'client_' + Math.random().toString(36).substring(2, 7),
                                        name: 'Client Découverte',
                                        address: 'France',
                                        loyaltyPoints: 100
                                    };
                                    webrtcVoiceService.startCall(
                                        medium.id.toString(),
                                        medium.name,
                                        medium.imageUrl,
                                        currentClient.id,
                                        currentClient.name,
                                        currentClient.address || 'France',
                                        currentClient.loyaltyPoints || 100
                                    );
                                }}
                                className="block w-full py-2.5 md:py-3 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 rounded-lg text-white font-bold shadow-lg hover:scale-102 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm md:text-base cursor-pointer"
                            >
                                <PhoneIcon className="w-4 h-4 md:w-5 md:h-5 animate-pulse" /> Appel Vocal en Direct
                            </button>
                            <p className="text-[9px] text-gray-400 mt-1 leading-tight text-center">
                                Communication audio HD chiffrée de navigateur à navigateur.
                            </p>
                        </div>

                        
                        <button 
                            onClick={() => { onClose(); onChatClick(medium); }}
                            className={`block w-full py-2.5 md:py-3 rounded-lg font-bold shadow-lg transition-all flex items-center justify-center gap-2 text-sm md:text-base cursor-pointer ${
                                isVip && isVipLocked
                                    ? 'bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black shadow-[0_0_20px_rgba(251,191,36,0.4)]'
                                    : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                            }`}
                        >
                            {isVip ? <CrownIcon className="w-4 h-4 md:w-5 md:h-5 text-current" /> : <MessageCircleIcon className="w-4 h-4 md:w-5 md:h-5" />}
                            <span>{isVip && isVipLocked ? 'Consulter VIP (1 000+ Astres)' : 'Consulter par Chat'}</span>
                        </button>
                    </div>
                </div>

                {/* Right Column: Description & Reviews */}
                <div className="w-full md:w-2/3 flex flex-col h-full bg-black/20 relative flex-1 min-h-0">
                    
                    {/* --- MOBILE TABS --- */}
                    <div className="flex md:hidden border-b border-white/10 bg-mystic-900 shrink-0">
                        <button 
                            onClick={() => setMobileTab('bio')}
                            className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${mobileTab === 'bio' ? 'text-gold-400 border-b-2 border-gold-400 bg-white/5' : 'text-gray-400'}`}
                        >
                            <InfoIcon className="w-4 h-4" /> Biographie
                        </button>
                        <button 
                            onClick={() => setMobileTab('reviews')}
                            className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${mobileTab === 'reviews' ? 'text-gold-400 border-b-2 border-gold-400 bg-white/5' : 'text-gray-400'}`}
                        >
                            <StarIcon className="w-4 h-4" /> Avis ({allReviews.length})
                        </button>
                    </div>

                    {/* Bio Section (Hidden on mobile if tab is reviews) */}
                    <div className={`p-6 border-b border-white/10 bg-white/5 shrink-0 z-10 shadow-md transition-all ${mobileTab === 'reviews' ? 'hidden md:block' : 'block'}`}>
                        <h3 className="text-lg font-bold text-gold-400 mb-3 flex items-center gap-2 border-b border-gold-500/30 pb-2 inline-block">
                            À propos de {medium.name}
                        </h3>
                        <p className="text-white text-sm leading-relaxed font-light text-justify max-h-48 md:max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                            {medium.description}
                        </p>
                        <div className="mt-4 flex gap-4 text-xs text-gray-400">
                            <div className="bg-black/40 px-3 py-1 rounded border border-white/10">Expérience: <span className="text-white font-bold">{medium.experienceYears} ans</span></div>
                            <div className="bg-black/40 px-3 py-1 rounded border border-white/10">Vus: <span className="text-white font-bold">{medium.reviews + 1000}+</span></div>
                        </div>
                    </div>

                    {/* Reviews List (Flex grow to take remaining space) */}
                    <div className={`flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar relative bg-black/20 ${mobileTab === 'bio' ? 'hidden md:flex flex-col' : 'flex flex-col'}`}>
                        
                        {/* Header inside ScrollView */}
                        <div className="flex flex-wrap items-center justify-between mb-4 bg-mystic-900/95 backdrop-blur-md p-3 rounded-lg border border-white/10 shadow-lg">
                            <h3 className="text-base md:text-lg font-bold text-white flex items-center gap-2">
                                Avis Clients
                                <span className="bg-gold-500 text-black text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm">
                                    {allReviews.length}
                                </span>
                            </h3>
                            
                            {!showReviewForm && (
                                <button 
                                    onClick={() => client ? setShowReviewForm(true) : openAuthModal()}
                                    className="text-[10px] md:text-xs bg-white/10 hover:bg-gold-500/20 text-gold-400 hover:text-white px-3 py-1.5 rounded border border-gold-500/30 transition-all"
                                >
                                    {client ? "Noter" : "Se connecter pour noter"}
                                </button>
                            )}
                        </div>

                        {/* FORMULAIRE D'AVIS */}
                        {showReviewForm && client && (
                            <div className="bg-gradient-to-br from-indigo-900/40 to-black p-4 rounded-xl border border-gold-500/30 mb-6 animate-fade-in-up shrink-0">
                                <h4 className="text-white text-sm font-bold mb-3">Laisser un avis en tant que <span className="text-gold-400">{client.name}</span></h4>
                                <form onSubmit={handleSubmitReview}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="text-xs text-gray-400">Note :</span>
                                        <div className="flex">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    key={star}
                                                    type="button"
                                                    onClick={() => setRating(star)}
                                                    className="p-1 focus:outline-none hover:scale-110 transition-transform"
                                                >
                                                    <StarIcon className={`w-5 h-5 ${star <= rating ? 'text-gold-400 fill-current' : 'text-gray-600'}`} />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <textarea
                                        value={reviewText}
                                        onChange={(e) => setReviewText(e.target.value)}
                                        placeholder="Racontez votre expérience..."
                                        className="w-full bg-black/50 border border-gray-600 rounded-lg p-3 text-white text-sm focus:border-gold-500 focus:outline-none mb-3 resize-none h-20 md:h-24"
                                        required
                                    />
                                    <div className="flex justify-end gap-2">
                                        <button 
                                            type="button" 
                                            onClick={() => setShowReviewForm(false)}
                                            className="px-3 py-1.5 text-xs text-gray-400 hover:text-white"
                                        >
                                            Annuler
                                        </button>
                                        <button 
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="bg-gold-600 hover:bg-gold-500 text-black font-bold px-4 py-1.5 rounded text-xs transition-colors disabled:opacity-50"
                                        >
                                            {isSubmitting ? 'Envoi...' : 'Publier'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                        
                        <div className="space-y-3 pb-20">
                            {allReviews.length > 0 ? (
                                allReviews.map((review, idx) => (
                                    <div key={`${review.id}-${idx}`} className={`p-3 md:p-4 rounded-xl border transition-colors ${review.isReal ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-white/5 border-white/5 hover:bg-white/10'}`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-br from-purple-900 to-indigo-900 border border-purple-500/30 flex items-center justify-center text-xs md:text-sm text-purple-200 font-bold shadow-inner shrink-0">
                                                    {review.author.charAt(0)}
                                                </div>
                                                <div>
                                                    <span className="block text-xs md:text-sm font-bold text-purple-200">
                                                        {review.author} 
                                                        {review.isReal && <span className="ml-2 text-[9px] bg-green-900 text-green-300 px-1 rounded border border-green-700">Vérifié</span>}
                                                    </span>
                                                    <div className="flex text-gold-500">
                                                        {[...Array(5)].map((_, i) => (
                                                            <StarIcon key={i} className={`w-2.5 h-2.5 ${i < review.rating ? 'fill-current' : 'text-gray-700'}`} />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="text-[9px] md:text-[10px] text-gray-500 bg-black/30 px-2 py-1 rounded border border-white/5 whitespace-nowrap">{review.date}</span>
                                        </div>
                                        <p className="text-gray-300 text-xs md:text-sm italic leading-relaxed pl-11 md:pl-12 border-l-2 border-white/5">
                                            "{review.text}"
                                        </p>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 text-center italic py-10">Aucun avis détaillé disponible pour le moment.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MediumProfileModal;
